/**
 * The four looks, and the two attributes that select one.
 *
 * A theme is nothing but a block of custom properties in styles.css, so this
 * file never names a colour: it sets `data-theme` and `data-mode` on <html> and
 * the stylesheet decides everything else. That is also why the swatches in
 * Settings can be rendered by putting `data-theme` on a <span> -- the variables
 * cascade to any element, so a preview is the real palette rather than a copy
 * of it that could drift.
 *
 * index.html sets both attributes before React mounts. Without that the page
 * paints the default theme first and then jumps, on every cold start.
 */

export const THEMES = [
  {
    id: 'pantry',
    name: 'Pantry',
    note: 'The original. Deep green, system type, soft edges.',
  },
  {
    id: 'market',
    name: 'Market',
    note: 'Persimmon on warm paper. Heavy type, round corners, photos to the edge.',
  },
  {
    id: 'homestead',
    name: 'Homestead',
    note: 'Cream and sage. Serif headings, and photos matted like a cookbook plate.',
  },
  {
    id: 'broadsheet',
    name: 'Broadsheet',
    note: 'Newsprint. Square corners, hairline rules, black doing the work of color.',
  },
];

export const MODES = [
  { id: 'system', name: 'Match device' },
  { id: 'light', name: 'Light' },
  { id: 'dark', name: 'Dark' },
];

const THEME_KEY = 'pantry:theme';
const MODE_KEY = 'pantry:mode';

export const DEFAULT_THEME = 'pantry';
export const DEFAULT_MODE = 'system';

/**
 * The web fonts each theme actually needs.
 *
 * Requested per theme rather than all at once because the default theme is
 * system-ui and needs none: loading five families on every cold start would
 * spend a phone's first seconds on faces most people never see. Injected from
 * here rather than from index.html so this list has one home -- the cost is a
 * brief fallback face on a cold start, which `display=swap` produces anyway.
 */
const THEME_FONTS = {
  market: 'family=Archivo:wght@400;500;600;800',
  homestead: 'family=Playfair+Display:wght@600;700&family=Lora:wght@400;500;600',
  broadsheet: 'family=Libre+Baskerville:wght@400;700&family=Libre+Franklin:wght@400;500;600;700',
};

const FONT_LINK_ID = 'pantry-theme-fonts';

/** Storage is unavailable in a locked-down browser, and a theme is not worth throwing over. */
const remember = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch { /* the attribute is still set; only the memory of it is lost */ }
};

const recall = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

export const readTheme = () => {
  const stored = recall(THEME_KEY);
  return THEMES.some((t) => t.id === stored) ? stored : DEFAULT_THEME;
};

export const readMode = () => {
  const stored = recall(MODE_KEY);
  return MODES.some((m) => m.id === stored) ? stored : DEFAULT_MODE;
};

function loadFonts(theme) {
  const families = THEME_FONTS[theme];
  if (!families) return;
  const href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
  // One link element, retargeted. Switching between two web-font themes must
  // not leave the previous theme's faces still being fetched.
  let link = document.getElementById(FONT_LINK_ID);
  if (!link) {
    link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    // Requested as CORS so the service worker gets a readable response to cache
    // instead of an opaque one it refuses: without this the faces are cached
    // offline and the @font-face rules that name them are not, which is the one
    // combination that looks exactly like the fonts failing to load.
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
}

/**
 * Keep the browser's own chrome the colour of the page it is framing.
 *
 * Read back from the stylesheet rather than kept in a table here: the resolved
 * --bg is the theme's answer for whichever mode ended up active, including the
 * one the OS chose when the mode is "system".
 */
function syncBrowserChrome() {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  const bg = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim();
  if (bg) meta.setAttribute('content', bg);
}

/**
 * Put a theme on the page and remember it.
 *
 * `mode` of "system" removes the attribute rather than writing a value: the
 * absence is what lets the prefers-color-scheme block in styles.css apply.
 */
export function applyTheme(theme, mode) {
  const root = document.documentElement;
  root.dataset.theme = theme;
  if (mode === 'system') delete root.dataset.mode;
  else root.dataset.mode = mode;

  remember(THEME_KEY, theme);
  remember(MODE_KEY, mode);

  loadFonts(theme);
  syncBrowserChrome();
}

/**
 * Catch up with what index.html already stamped on <html>.
 *
 * The attributes are set before React mounts, but the font request and the
 * browser-chrome colour are not -- index.html deliberately knows nothing about
 * either. Called once on mount.
 */
export function hydrateTheme() {
  loadFonts(readTheme());
  syncBrowserChrome();
}
